# Route: /tracking/records/:recordId
# View full tracking record with details, signatures, amendments
# Permission: TrackingRecord:read

page-tracking-record-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.tracking.detail.meta_title
    route: /tracking/records/:recordId
    layout: shell
    auth: required
  data:
    record:
      model: TrackingRecord
      key: $route.recordId
      required: true
    # Request body for the Export-PDF header action. The button's custom
    # direct-fetch action serialises this node as the POST body — the
    # endpoint contract is ExportPdfRequest { template: "detail", entryId }
    # (src/Xp87.Api/Endpoints/Tracking/ExportPdfEndpoint.cs). Local form-style
    # model (no source/key): seeded at fetch time, entryId copied from the
    # already-fetched entry — MUST stay declared after `record`.
    exportRequest:
      model: ExportPdfForm
      initial:
        template: "detail"
        entryId: $data.record.id
    attachments:
      model: Attachment[]
      key: $route.recordId
      refresh: on_mount
    history:
      model: TrackingRecordHistory[]
      key: $route.recordId
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - record-detail-headerbar
    - record-detail-context-header
    - record-detail-conflict-alert
    - record-detail-superseded-alert
    - record-detail-info
    - record-detail-structured
    - record-detail-text
    - record-detail-attachments
    - record-detail-signature-timeline
    - record-detail-history

# ── Header ───────────────────────────────────────────────────────────────────

# Header bar (title + actions on one row) — generic flex composition so the
# four action buttons sit ON the page title's line (justify space-between),
# not in a separate row below it. The buttons stay full Button nodes (Sign
# navigate, Amend navigate, Audit navigate+query, Export-PDF custom+download)
# composed onto the title line via this row.
record-detail-headerbar:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: space-between
    align: center
    gap: $spacing.md
    wrap: wrap
  children:
    - record-detail-header
    - record-detail-actions

record-detail-header:
  type: PageHeader
  back_route: /tracking/records
  title_prefix: $t.tracking.detail.title_prefix
  bind:
    content: record.formName

# F80 FR-3 — Asset · Tracking · Shift chips, bound to the entry's own pinned
# context (the entry timestamp drives the shift chip, never the render clock).
record-detail-context-header:
  type: Widget
  ref: xp87-tracking-context-header
  params:
    context-type: $data.record.contextType
    context-display-name: $data.record.contextDisplayName
    tracking-name: $data.record.formName
    tracking-version: $data.record.formVersionNumber
    timestamp: $data.record.timestamp

# ── Conflict alert (concurrent-modification 409) ─────────────────────────────
# The <xp87-inline-alert> widget is default-hidden by its own shadow
# CSS (reflected `open`, no timer). xp87-error-router.js sets `.open = true` on a
# 409 xp85-action-complete and clears it on navigation, so the operator sees
# their action stale-failed and the entry was modified by another user since they
# opened the page. Same node id as before so findXp85Node / assert-visible resolve.
#
# Cross-refs:
#   - tests/5-e2e/tracking/215-tracking-ui-error-control.scenario.yaml § 409
#   - ui/src/widgets/xp87-inline-alert/xp87-inline-alert.js
#   - ui/src/widgets/xp87-error-router/xp87-error-router.js
record-detail-conflict-alert:
  type: Widget
  ref: xp87-inline-alert
  params:
    heading: $t.errors.conflict.title
    body: $t.errors.conflict.body
    action-label: $t.errors.conflict.btn_refresh

# Compliance safety net (21 CFR Part 11 record integrity): a superseded revision
# normally redirects to its chain head (FR-1, xp87-tracking-record-detail.js). If
# that redirect cannot run — the secondary `history` fetch failed or raced — this
# banner still fires off the synchronously-available `record.isAmended`, so the
# primary content area is NEVER ambiguously shown as current. Gated by
# visible_when so it renders only for a superseded revision; `open` makes the
# inline-alert visible when rendered.
record-detail-superseded-alert:
  type: Widget
  ref: xp87-inline-alert
  visible_when: record.isAmended
  params:
    open: true
    heading: $t.tracking.detail.superseded_title
    body: $t.tracking.detail.superseded_body

# F75 #178 — one right-aligned action row following the button-variant
# convention: ONE filled primary (Sign, go), one outlined secondary (Amend,
# info — caution is reserved for warnings), ghost tertiaries (Audit, Export).
# Buttons stay standalone nodes (not PageHeader `actions:` shorthand, which
# can't express custom/download) so Tier 5 scenarios keep addressing them by
# id; record-detail-headerbar composes this row onto the page title's line.
#
# M1 rendering sweep (phone-width) — these four buttons used to carry
# `responsive.sm.visible: false`, hiding the ENTIRE action row (including
# Sign and Amend — the two actions M1 explicitly requires from a phone) at
# phone width. Operators recording/correcting entries from a phone had no way
# to sign or amend. Fix: keep every button visible at every width; `wrap:
# wrap` here lets the row reflow onto a second line on narrow viewports
# instead of overflowing — each button keeps its full touch-target size
# (min_height: $size.touch-target already set below) rather than shrinking
# or hiding.
record-detail-actions:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: flex-end
    align: center
    gap: $spacing.sm
    wrap: wrap
  children:
    - record-detail-btn-sign
    - record-detail-btn-amend
    - record-detail-btn-audit
    - record-detail-btn-export-pdf

record-detail-btn-sign:
  type: Button
  variant: filled
  color: go
  content: $t.tracking.detail.btn_sign
  a11y:
    label: $t.tracking.detail.btn_sign
  action:
    type: navigate
    route: /tracking/records/:recordId/sign
    params:
      recordId: $row.recordId
    # F79 — return to this detail after sign (Back / Cancel / on_success).
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# F-A RBAC: the amend affordance is gated on TrackingRecord:amend so an Operator
# (who lacks it) is not offered an action the API refuses with 403. The API's
# RequirePermission stays the security boundary; this is the matching UX
# affordance. The gate sits on the Button itself, beside its `visible_when`:
# `permission:` is common anatomy, so `RenderChild` wraps a node that declares
# one whatever its type, keyed on the annotation rather than on the family
# (`HtmlTemplateGenerator.cs`, and `RequiresPermissionAnnotation` answers the
# different question of which types MUST declare one).
record-detail-btn-amend:
  type: Button
  permission: "TrackingRecord:amend"
  # A successful amendment supersedes this revision. Do not offer an action
  # that the API must reject with "Entry has already been amended."
  visible_when: record.status != 'Amended'
  variant: outlined
  color: info
  content: $t.tracking.detail.btn_amend
  a11y:
    label: $t.tracking.detail.btn_amend
  action:
    type: navigate
    route: /tracking/records/:recordId/amend
    params:
      recordId: $row.recordId
    # F79 — return to this detail after amend (Back / Cancel / on_success).
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

record-detail-btn-audit:
  type: Button
  variant: ghost
  content: $t.tracking.detail.btn_audit
  icon: $icon.clipboard-text
  a11y:
    label: $t.tracking.detail.btn_audit
  # F79 flagship — entity detail → filtered audit trail → Back. Navigates to the
  # canonical /audit list (NOT a per-entity audit page — no-lasagna L3) with the
  # FORWARD channel seeding its filter bar (entityType + entityId → GET
  # /api/audit?entityType=TrackingRecord&entityId=<id>) and the RETURN channel so
  # /audit (a root page) shows a contextual Back to this detail.
  action:
    type: navigate
    route: /audit
    query:
      entityType: TrackingRecord
      entityId: $route.recordId
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# F53b E.6 — Export-PDF action for a single entry. POSTs the exportRequest
# data node ({ template: "detail", entryId }) as the JSON body via the
# custom direct-fetch action (method+url+body — the only ActionSpec shape
# the emitter compiles to a real fetch; the previous `endpoint:`+`params:`
# keys are not ActionSpec fields, so the request went out with NO body and
# the endpoint answered 400 "Request body is required").
record-detail-btn-export-pdf:
  type: Button
  variant: ghost
  content: $t.tracking.detail.btn_export_pdf
  icon: $icon.download-simple
  a11y:
    label: $t.tracking.detail.btn_export_pdf_label
  action:
    type: custom
    method: POST
    url: /api/tracking/export-pdf
    body: exportRequest
    download: true
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

# ── Record info card ─────────────────────────────────────────────────────────

# Compact header (Option B). The elevated card carries two rows: an identity
# line — context › form with the amendment-version + status badges pushed to
# the trailing edge — and a muted author · timestamp meta line. The vertical
# label/value grid (F75) is replaced by this horizontal grouping; the value node
# ids (`record-info-*-value`, `record-detail-status`) are unchanged so Tier-4/5
# scenarios keep addressing them. Labels are dropped (the two-line grouping is
# self-describing). `wrap: wrap` on each row keeps it responsive: on a narrow
# viewport the badges drop below the name and the meta values reflow.
record-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.sm
    responsive:
      sm:
        padding: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - record-detail-identity-line
    - record-detail-meta-line

record-detail-identity-line:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: space-between
    align: center
    gap: $spacing.md
    wrap: wrap
  children:
    - record-detail-identity-name
    - record-detail-identity-badges

record-detail-identity-name:
  type: Section
  spatial:
    display: flex
    direction: row
    align: baseline
    gap: $spacing.sm
    wrap: wrap
  children:
    - record-info-context-value
    - record-info-form-value

record-info-context-value:
  type: Text
  bind:
    content: record.contextDisplayName
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# The form name reads as a secondary qualifier of the context subject
# (muted weight/colour), so the pair separates visually without a decorative
# separator glyph (which would be an un-localizable raw content literal).
record-info-form-value:
  type: Text
  bind:
    content: record.formName
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

record-detail-identity-badges:
  type: Section
  spatial:
    display: flex
    direction: row
    align: center
    gap: $spacing.sm
    wrap: wrap
  children:
    - record-detail-version
    - record-detail-status

# Compact "v{n} / {m}" amendment-version chip bound to the server-computed
# versionNumber/versionCount. Renders nothing for a single-version entry, so the
# badge only appears once the entry has actually been amended — the at-a-glance
# "there are multiple versions" signal.
record-detail-version:
  type: Widget
  ref: xp87-tracking-version-badge
  params:
    version-number: $data.record.versionNumber
    version-count: $data.record.versionCount

# The ONE status indicator on this page (a duplicate standalone badge used
# to float alone below the actions row — no-lasagna consolidation). Keeps
# the node id `record-detail-status` that Tier 4/5 tests address.
record-detail-status:
  type: StatusBadge
  bind:
    content: record.status
    variant:
      when: "record.status == 'Complete'"
      value: go
      else:
        when: "record.status == 'PendingReview'"
        value: caution
        else:
          when: "record.status == 'Amended'"
          value: stop
          else: info

record-detail-meta-line:
  type: Section
  spatial:
    display: flex
    direction: row
    align: baseline
    gap: $spacing.lg
    wrap: wrap
  children:
    - record-info-author-value
    - record-info-timestamp-value

# Binds the server-enriched display name (TrackingDisplayNameEnricher resolves
# users.user_profiles; falls back to the raw author id when no profile).
record-info-author-value:
  type: Text
  bind:
    content: record.authorDisplayName
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

record-info-timestamp-value:
  type: Text
  format: compliance-datetime
  bind:
    content: record.timestamp
  tokens:
    font_size: $font.size.sm
    font_family: $font.family.mono
    color: $color_scheme.light.color.on-surface-muted

# ── Narrative text ───────────────────────────────────────────────────────────

record-detail-text:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - record-text-title
    - record-text-content

record-text-title:
  type: Text
  content: $t.tracking.detail.narrative
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

record-text-content:
  type: Text
  bind:
    content: record.text
  tokens:
    font_size: $font.size.md

# ── Structured fields (G9) ────────────────────────────────────────────────────
# Read-only render of entry.structuredData joined with the template's field defs
# (label + type) by the xp87-tracking-structured-fields widget. The widget reads
# its `record` param, bound to $data.record, and fetches the entry's template
# itself by formId — the version PINNED at write time (F80 FR-6 / ADR-181
# rules 3–4), so historical entries render against the template as it was when
# captured, never the current version. It owns the empty
# case (renders nothing when no field has a value) — so there is NO data_states.empty
# here (single empty path, no-lasagna).
record-detail-structured:
  type: Widget
  ref: xp87-tracking-structured-fields
  params:
    record: $data.record
    title: $t.tracking.detail.structured_title
    label-bool-true: $t.tracking.structured.bool_true
    label-bool-false: $t.tracking.structured.bool_false
    label-fetch-error: $t.tracking.structured.fetch_error
    label-orphan-note: $t.tracking.structured.orphan_note

# ── Attachments ──────────────────────────────────────────────────────────────

record-detail-attachments:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - record-attachments-header
    - record-attachments-table

record-attachments-header:
  type: Section
  spatial:
    display: flex
    justify: space-between
    align: center
  children:
    - record-attachments-title
    - record-attachments-upload-btn

record-attachments-title:
  type: Text
  content: $t.tracking.attachments.title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

# The upload chain resolves the parent entry id from the page data context
# (dispatcher: target?.id ?? target?.entry?.id → POST
# /api/tracking/records/{id}/attachments) and the dispatcher fires
# xp85-data-changed on success, which re-runs _fetchPageData and refreshes
# the attachments table. The historical "no-op" was a swallowed 422:
# AddAttachmentHandler whitelists content types (AttachmentSettings
# defaults) and the picker had no `accept` filter, so any other file type
# was rejected server-side with only a console.error. Keep this list in
# sync with src/Xp87.Infrastructure/Storage/AttachmentSettings.cs.
record-attachments-upload-btn:
  type: Button
  # XP85-UX-004 — a Button states its emphasis via variant+color (the canonical
  # button affordance), not a full info-surface tint. Outlined info keeps it a
  # secondary action next to the page's primary buttons.
  variant: outlined
  color: info
  content: $t.tracking.attachments.btn_upload
  action:
    type: upload
    model: Attachment
    accept: "image/jpeg,image/png,application/pdf,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.jpg,.jpeg,.png,.pdf,.txt,.csv,.xlsx"
  tokens:
    radius: $radius.md
  spatial:
    min_height: touch-safe
    padding_inline: $spacing.md
  states:
    hover:
      opacity: 0.85

record-attachments-table:
  type: Table
  bind:
    rows: attachments
  empty: record-attachments-empty
  columns:
    - key: filename
      label: $t.tracking.attachments.col_filename
      tokens:
        font_size: $font.size.sm
    - key: contentType
      label: $t.tracking.attachments.col_type
      tokens:
        font_size: $font.size.sm
    - key: sizeBytes
      label: $t.tracking.attachments.col_size
      format: filesize
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: uploadedBy
      label: $t.tracking.attachments.col_uploaded_by
      tokens:
        font_size: $font.size.sm
    - key: uploadedAt
      label: $t.tracking.attachments.col_uploaded_at
      format: compliance-datetime
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
  # Clicking a row fetches the file with the user's bearer token and
  # dispatches xp85-preview so the <xp85-preview-panel> shows it inline
  # (images / PDFs) or downloads it (other types). type: download is
  # required — type: navigate would set window.location.hash to an API
  # URL, which the hash router can't handle and which skips the auth
  # header the endpoint requires.
  row_action:
    type: download
    route: /api/tracking/records/:recordId/attachments/:fileId
    params:
      recordId: $route.recordId
      fileId: $row.fileId
  a11y:
    label: $t.tracking.attachments.table_label
  tokens:
    border: $border.default
    radius: $radius.md

record-attachments-empty:
  type: EmptyState
  content: $t.tracking.attachments.empty
  body: $t.tracking.attachments.empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# ── Signatures (G2) ──────────────────────────────────────────────────────────

# A signature display: who signed, as what, when and with which meaning (21 CFR 11.50).
# signature_surface holds this card and everything it mounts out of the customization
# targets — a reviewer reads a signature as the platform rendered it (Feature #1913).
record-detail-signature-timeline:
  type: Card
  signature_surface: true
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - record-signatures-title
    - record-signatures-table
    - record-signatures-next

record-signatures-title:
  type: Text
  content: $t.tracking.detail.signatures
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

record-signatures-table:
  type: Table
  bind:
    rows: record.signatures
  empty: record-signatures-empty
  columns:
    - key: slot
      label: $t.tracking.detail.col_sig_slot
      tokens:
        font_size: $font.size.sm
    # signedByDisplayName is the server-enriched human signer name
    # (TrackingSignatureDto.SignedByDisplayName, resolved from
    # users.user_profiles; falls back to the sign-time value).
    - key: signedByDisplayName
      label: $t.tracking.detail.col_sig_user
      tokens:
        font_size: $font.size.sm
    - key: timestamp
      label: $t.tracking.detail.col_sig_timestamp
      format: compliance-datetime
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: meaning
      label: $t.tracking.detail.col_sig_meaning
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.tracking.detail.signatures
  tokens:
    border: $border.default
    radius: $radius.md

record-signatures-empty:
  type: EmptyState
  content: $t.tracking.detail.signatures_empty
  body: $t.tracking.detail.signatures_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# FR-3d — the next required signer, human-readable. Binds the server-computed,
# caller-relative NextSignerLabel ("Complete" / "Awaiting you" / "Awaiting
# <role>"), the SAME field the entry list renders, so list and detail agree. This
# consolidates the previous node that bound the raw integer slot index — one
# next-signer mechanism, no divergent twin.
record-signatures-next:
  type: Text
  bind:
    content: record.nextSignerLabel
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# ── Revision history (G1) ────────────────────────────────────────────────────

record-detail-history:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - record-history-title
    - record-version-viewer

record-history-title:
  type: Text
  content: $t.tracking.detail.history_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

# FR-6 — the version viewer owns the whole revision history: it browses every
# amendment version (data.history — the ascend-to-root FR-2 chain) with a
# tab/dropdown selector, renders each version's full snapshot against its own
# pinned template, and highlights changes since the previous version. It owns the
# single-version and history-missing states itself, so there is no separate List /
# item-template / empty node here (one history mechanism — no-lasagna).
record-version-viewer:
  type: Widget
  ref: xp87-tracking-version-viewer
  params:
    record: $data.record
    history: $data.history
    title: $t.tracking.versions.title
    label-version: $t.tracking.versions.tab_label
    label-current: $t.tracking.versions.current_badge
    label-changed: $t.tracking.versions.changed_badge
    label-was: $t.tracking.versions.was_label
    label-select: $t.tracking.versions.select_label
    label-error: $t.tracking.versions.error
    structured-title: $t.tracking.detail.structured_title
    label-bool-true: $t.tracking.structured.bool_true
    label-bool-false: $t.tracking.structured.bool_false
    label-fetch-error: $t.tracking.structured.fetch_error
    label-orphan-note: $t.tracking.structured.orphan_note
